/**
 * Cross-field WARNINGS — non-blocking sanity checks that span two fields (or
 * a field and today's date) on the same form state.
 *
 * These are deliberately warnings, not validation errors: B2's EDGE-01/EDGE-02
 * findings (regression run 2026-07-12/13) confirmed the portal silently
 * accepts a marriage date before the relationship-began date and an expired
 * passport with no flag at all, but there is no Immi evidence that either
 * combination should hard-block submission (an expired passport, for
 * instance, is routinely still submitted while a renewal is in train). The
 * fix-batch plan explicitly accepts warn OR block for these — this module
 * implements warn.
 *
 * Kept pure (no React, no Supabase) so it is trivially unit-testable and can
 * be evaluated on every render without side effects. It is NOT wired into
 * validateForm.ts / stageValidationErrors — a warning here must never block
 * Next/Submit or fail the server-side latch re-validation.
 */

import { isValidFullDate } from './schema-renderer/validation/calendarDate';
import { analyseAddressHistoryCoverage, formatGaps } from './schema-runtime/addressHistoryCoverage';

export interface CrossFieldWarning {
  /** Stable identifier for this warning rule (safe to use as a React key). */
  id: string;
  /** canonical_name the warning is about, for inline/near-field display. */
  field: string;
  /** Human-readable, client-safe message. */
  message: string;
  /**
   * True when a matching HARD rule in validateForm blocks submission until
   * this is resolved (e.g. address-history gaps). The banner renders these
   * as must-fix, not advisory, and the section nav flags the section.
   */
  blocking?: boolean;
}

export interface CrossFieldWarningsOptions {
  /** schema.form_id of the form being evaluated. Gates form-specific rules
   *  (e.g. the PMV intended-marriage-date window, which only applies to
   *  47SP_300_PMV). Omit to evaluate every rule regardless of form. */
  formId?: string | null;
  /** Override "today" for deterministic tests. Defaults to `new Date()`. */
  today?: Date;
}

/**
 * Read a canonical_name path from form state, tolerating both the flat
 * dotted-key shape (RHF's raw watch()/getValues() before expansion) and the
 * nested object shape (post expandFormState). Mirrors validateForm.ts's
 * dual-shape reader so this module never disagrees with the submit gate
 * about what a given canonical_name currently holds.
 */
function readPath(state: Record<string, unknown>, path: string): unknown {
  if (Object.prototype.hasOwnProperty.call(state, path)) {
    return state[path];
  }
  const parts = path.split('.');
  let cur: unknown = state;
  for (const p of parts) {
    if (cur === null || typeof cur !== 'object' || Array.isArray(cur)) return undefined;
    cur = (cur as Record<string, unknown>)[p];
  }
  return cur;
}

function asNonEmptyString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

function midnight(d: Date): Date {
  const out = new Date(d.getTime());
  out.setHours(0, 0, 0, 0);
  return out;
}

function addMonths(date: Date, months: number): Date {
  const out = new Date(date.getTime());
  out.setMonth(out.getMonth() + months);
  return out;
}

const PMV_FORM_ID = '47SP_300_PMV';
const PMV_INTENDED_MARRIAGE_WINDOW_MONTHS = 9;

/**
 * Compute every applicable cross-field warning for the given form state.
 * Returns an empty array when nothing is flagged (including when the
 * relevant fields are simply blank — a warning never fires on missing data,
 * only on data that looks internally inconsistent).
 */
export function computeCrossFieldWarnings(
  state: Record<string, unknown>,
  options: CrossFieldWarningsOptions = {},
): CrossFieldWarning[] {
  const warnings: CrossFieldWarning[] = [];
  const today = midnight(options.today ?? new Date());

  // (a) Marriage date earlier than the date the relationship began.
  const dateOfMarriage = asNonEmptyString(readPath(state, 'applicant.relationship.date_of_marriage'));
  const dateRelationshipBegan = asNonEmptyString(
    readPath(state, 'applicant.relationship.date_relationship_began'),
  );
  if (
    dateOfMarriage &&
    dateRelationshipBegan &&
    isValidFullDate(dateOfMarriage) &&
    isValidFullDate(dateRelationshipBegan) &&
    dateOfMarriage < dateRelationshipBegan
  ) {
    warnings.push({
      id: 'marriage-before-relationship-began',
      field: 'applicant.relationship.date_of_marriage',
      message:
        'The date of marriage is earlier than the date the relationship began. Double check these two dates — marriage usually falls on or after the relationship start date.',
    });
  }

  // (b) Passport expiry date in the past — checked for both the applicant's
  // and the sponsor's primary passport (whichever is present in this form's
  // state; a field absent from this form's schema simply never appears).
  const passportChecks: Array<{ field: string; who: string }> = [
    { field: 'applicant.passport.date_of_expiry', who: "the applicant's" },
    { field: 'sponsor.passport.date_of_expiry', who: "the sponsor's" },
  ];
  for (const { field, who } of passportChecks) {
    const expiry = asNonEmptyString(readPath(state, field));
    if (expiry && isValidFullDate(expiry)) {
      const expiryDate = midnight(new Date(`${expiry}T00:00:00`));
      if (expiryDate.getTime() < today.getTime()) {
        warnings.push({
          id: `passport-expired:${field}`,
          field,
          message: `The expiry date on ${who} passport is in the past. Check whether it has been renewed and update the details here if so.`,
        });
      }
    }
  }

  // (c) PMV (300) intended marriage date more than 9 months after today.
  if (!options.formId || options.formId === PMV_FORM_ID) {
    const intendedMarriageDate = asNonEmptyString(
      readPath(state, 'applicant.noim.intended_marriage_date'),
    );
    if (intendedMarriageDate && isValidFullDate(intendedMarriageDate)) {
      const intendedDate = midnight(new Date(`${intendedMarriageDate}T00:00:00`));
      const windowEnd = addMonths(today, PMV_INTENDED_MARRIAGE_WINDOW_MONTHS);
      if (intendedDate.getTime() > windowEnd.getTime()) {
        warnings.push({
          id: 'pmv-intended-marriage-too-far',
          field: 'applicant.noim.intended_marriage_date',
          message:
            'The intended marriage date is more than 9 months from today. A Prospective Marriage (subclass 300) visa generally requires the marriage to take place within 9 months of grant — check this date is realistic.',
        });
      }
    }
  }

  // (d) Partner forms: incomplete 10-year address history. The client sees
  // this WHILE they type (a warning, not a block); the matching HARD rule in
  // validateForm stops submission until the history is gapless. Fires only
  // once they have started the list — a warning never fires on missing data.
  if (!options.formId || PARTNER_FORM_IDS.has(options.formId)) {
    const hasLived = readPath(state, 'applicant.countries_resided.has_lived_outside_citizenship');
    const entries = readPath(state, 'applicant.countries_resided.entries');
    if (hasLived === 'Yes' && Array.isArray(entries) && entries.length > 0) {
      const coverage = analyseAddressHistoryCoverage(entries, today);
      if (!coverage.complete) {
        warnings.push({
          id: 'address-history-gaps',
          blocking: true,
          field: 'applicant.countries_resided.entries',
          message: `You have entered a gap and there is no address history for that gap. Please fill in the gap. Missing periods: ${formatGaps(coverage.gaps)}. The Department needs the full 10 years covered, and the form cannot be finished until it is.`,
        });
      }
    }
  }

  // (e) Partner forms incl. stage 2: fewer than two supporting witnesses.
  // min_count 2 already hard-blocks submission; this makes the shortfall
  // visible the moment they stop at one, instead of at submit time.
  if (!options.formId || WITNESS_FORM_IDS.has(options.formId)) {
    const witnesses = readPath(state, 'applicant.witnesses');
    if (Array.isArray(witnesses) && witnesses.length === 1) {
      warnings.push({
        id: 'witnesses-minimum-two',
        blocking: true,
        field: 'applicant.witnesses',
        message:
          'Please enter at least two supporting witnesses. The more the better - each must be an Australian citizen or permanent resident over 18.',
      });
    }
  }

  return warnings;
}

const PARTNER_FORM_IDS = new Set([
  '47SP_820_onshore_stage1',
  '47SP_309_offshore_stage1',
  '47SP_300_PMV',
]);

const WITNESS_FORM_IDS = new Set([...PARTNER_FORM_IDS, '47SP_stage2']);
